import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Square } from "lucide-react";
import { useCallTimer, formatCallDuration } from "@/stores/callTimer.store";
import { useCallElapsedSeconds } from "@/hooks/useCallElapsedSeconds";
import { usePermissions } from "@/hooks/usePermissions";
import { formatPhone, isPhoneNumber, phoneHref } from "@/lib/phone";

/**
 * Persistent emerald banner pinned to the top of the app shell while
 * a call is running. Visible on every page so the user can see / stop
 * the call from anywhere.
 *
 * Stop navigates back to the company the call belongs to so the
 * LogCallDialog (mounted in CompanyDetailPage) can open with the
 * pre-filled payload. EXCEPTION: on the Call Center page the inline
 * outcome form in ActiveSession consumes the pendingLog itself (and
 * calls clearPendingLog before LogCallDialog can see it), so we skip
 * the navigation entirely — otherwise we'd unmount ActiveSession
 * mid-stop and the dialog would pop on CompanyDetailPage instead of
 * the inline form the operator expects.
 *
 * Also installs a beforeunload guard while the call is running —
 * closing the tab mid-call now prompts the browser's native "Are you
 * sure?" confirmation so the timer state isn't accidentally lost.
 */
export function CallTimerBanner() {
  const active = useCallTimer((s) => s.active);
  const slug = useCallTimer((s) => s.slug);
  const companyId = useCallTimer((s) => s.companyId);
  const companyName = useCallTimer((s) => s.companyName);
  const contactName = useCallTimer((s) => s.contactName);
  const contactPhone = useCallTimer((s) => s.contactPhone);
  const stopCall = useCallTimer((s) => s.stopCall);
  const elapsed = useCallElapsedSeconds();
  const navigate = useNavigate();
  const location = useLocation();
  // The banner only matters for users who can start the timer in
  // the first place. A stale store entry on a permission-revoked
  // user shouldn't keep painting it across the app.
  const canStartCallTimer = usePermissions().canStartCallTimer;

  // Guard against closing the tab mid-call. Browsers ignore the
  // returnValue text and show a generic confirmation, but the
  // assignment still triggers the dialog.
  useEffect(() => {
    if (!active) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue =
        "A call is in progress. Are you sure you want to leave?";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", onBeforeUnload);
  }, [active]);

  if (!active || !canStartCallTimer) return null;

  function handleStop() {
    // Call Center page owns its own outcome flow — the inline form in
    // ActiveSession picks up the pendingLog and calls clearPendingLog,
    // so LogCallDialog never gets a turn. Navigating away would
    // unmount ActiveSession mid-stop and let CompanyDetailPage open
    // the dialog instead, defeating the inline UX entirely. Match the
    // route by suffix so any nested call-center sub-path is covered.
    const onCallCenter = location.pathname.endsWith("/call-center");
    if (!onCallCenter && slug && companyId != null) {
      // Navigate back to the calling company first so the dialog
      // mounts in the right project context, *then* trigger the stop —
      // the page's pendingLog effect picks it up after route resolution.
      navigate(`/projects/${slug}/companies/${companyId}`);
    }
    stopCall();
  }

  const companyHref =
    slug && companyId != null
      ? `/projects/${slug}/companies/${companyId}`
      : null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-4 border-b border-emerald-200 bg-emerald-50 px-6 py-1.5"
      style={{ height: 36 }}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-rose-500"
          aria-hidden
        />
        <span className="truncate text-sm font-medium text-emerald-800">
          Calling {contactName || "—"}
          {companyHref && companyName && (
            <>
              {" · "}
              <Link
                to={companyHref}
                className="text-emerald-700 hover:underline"
              >
                {companyName}
              </Link>
            </>
          )}
        </span>
        {isPhoneNumber(contactPhone) && (
          <a
            href={phoneHref(contactPhone) ?? undefined}
            className="font-mono text-xs text-emerald-700 hover:underline"
          >
            {formatPhone(contactPhone)}
          </a>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="font-mono text-sm font-semibold tabular-nums text-emerald-900">
          {formatCallDuration(elapsed)}
        </span>
        <button
          type="button"
          onClick={handleStop}
          aria-label="Stop call"
          className="flex h-6 w-6 items-center justify-center rounded-md bg-rose-500 text-white transition-colors hover:bg-rose-600"
        >
          <Square className="h-3 w-3 fill-current" />
        </button>
      </div>
    </div>
  );
}

/** Banner height (px). Used by AppLayout to offset other fixed
 *  surfaces (sidebar top offset etc) when the banner is visible. */
export const CALL_TIMER_BANNER_HEIGHT = 36;
